/**
 * Phase 4 penalty stage — a <video> + overlay <canvas> pair that draws an AR
 * filter (mustache/nose/glasses/tears) anchored to face landmarks, plus photo
 * capture. Plain 2D canvas, deliberately NOT PixiJS (this only runs during
 * the post-match screen, not the gameplay loop) — EXCEPT for the 3 mesh-warp
 * filters (턱쭉/홀쭉이/볼빵빵), which delegate to a small owned `WarpRenderer`
 * (client/warp.ts, PixiJS MeshPlane). `setFilter()`'s kind decides the mode:
 * overlay draws its SVG on this class's own 2D canvas as before; warp fades
 * the <video> to `opacity:0` (kept playing) and shows the WarpRenderer's
 * canvas instead — this class's 2D canvas then only keeps its face-lost
 * hint/dim duty. The WarpRenderer persists for the whole stage session (lazy
 * create on first warp pick, `destroy()`d in `stop()`) so switching between
 * overlay and warp filters never tears down/recreates a WebGL context. A
 * third kind, "label" (하남자/하여자/포테토남— meme text above the head), is
 * video-visibility-wise identical to overlay (no warp involved) but has no
 * image asset — it's drawn straight onto the 2D canvas via `drawLabel()`'s
 * `fillText`, not `drawImage`.
 *
 * Two roles share this ONE class (see client/main.ts wiring):
 *  - winner: `video` shows the opponent's REMOTE stream; `driveTracker` is a
 *    SECOND, independent FaceLandmarker instance (see face.ts) that this
 *    class itself throttles to ~15Hz and closes on `stop()`.
 *  - loser: `video` shows the player's OWN local camera stream (CSS-mirrored
 *    by the caller); landmarks are read from the ALREADY-RUNNING main
 *    session tracker via `getLandmarks` — no second detector, `driveTracker`
 *    is omitted.
 *
 * Cover-fit + mirror math mirrors render.ts's GameRenderer (kept local here
 * since that version is wired to the Pixi app's own screen size).
 *
 * `getLandmarks` callers (main.ts) are expected to return `null` once the
 * underlying tracker's `hasFace` is false — FaceTrackerImpl otherwise keeps
 * the last-seen landmarks around forever. This class then applies its OWN
 * short grace window (FACE_LOST_GRACE_MS) before actually treating that as
 * "face lost", so a single-frame blink doesn't flash the hint / dim the
 * canvas / freeze the filter mid-blink.
 */

import type { NormalizedLandmark } from "@mediapipe/tasks-vision";
import type { FaceTracker } from "./face.js";
import { FILTERS, isLabelFilterId, isOverlayFilterId, isWarpFilterId } from "./types.js";
import type { FilterId, LabelFilterId, OverlayFilterId } from "./types.js";
import { WarpRenderer } from "./warp.js";

/**
 * One DOM binding for a stage — grouped so a PenaltyStage instance can bind
 * to WHICHEVER set of elements the caller owns (the matchResult screen's
 * #penalty-* ids, or the try-on screen's own #tryon-* ids). `container` is
 * the whole box; PenaltyStage itself only ever shows/hides it via
 * `setVisible()` — video/canvas/hint are read every frame as usual.
 */
export interface PenaltyStageElements {
  container: HTMLElement;
  video: HTMLVideoElement;
  canvas: HTMLCanvasElement;
  /** Shown (and canvas dimmed) whenever no face has been seen for FACE_LOST_GRACE_MS. */
  hint: HTMLElement;
}

export interface PenaltyStageOptions {
  elements: PenaltyStageElements;
  /** True for a self view (video is CSS `scaleX(-1)`d by the caller). */
  mirrored: boolean;
  /** Latest landmarks, or null once the source tracker's `hasFace` is false. Called every frame. */
  getLandmarks: () => NormalizedLandmark[] | null;
  /**
   * Present ONLY when this stage owns an independent detector (the
   * matchResult winner's second tracker against the remote video): this
   * stage calls `.update()` on it (throttled to ~15Hz) and `.close()`s it in
   * `stop()`. Omit when landmarks come from an externally-driven tracker
   * (matchResult loser, or try-on — both reuse the main session tracker).
   */
  driveTracker?: FaceTracker;
}

/** Detection cadence for the winner's own second tracker (item 8: thermal ≤15Hz). */
const DRIVE_INTERVAL_MS = 1000 / 15;
/** How often (in frames) the pre-generated share/save blob refreshes. */
const BLOB_REFRESH_FRAMES = 20;
/** Grace window before a face-lost reading (null landmarks) is treated as real, not a blink. */
const FACE_LOST_GRACE_MS = 500;
const EMA_ALPHA = 0.4;
const WATERMARK_TEXT = "냠냠대전";
/** Matches render.ts/PLAN.md's backing-resolution cap for canvas surfaces. */
const DPR_CAP = 1.5;

interface CoverMap {
  scale: number;
  offX: number;
  offY: number;
  vw: number;
  vh: number;
}

/** Same cover-fit math as render.ts's `layout()`/`videoNormToField`. */
function computeCoverMap(video: HTMLVideoElement, canvasW: number, canvasH: number): CoverMap {
  const sw = canvasW || 1;
  const sh = canvasH || 1;
  const vw = video.videoWidth > 0 ? video.videoWidth : sw;
  const vh = video.videoHeight > 0 ? video.videoHeight : sh;
  const scale = Math.max(sw / vw, sh / vh);
  return { scale, offX: (sw - vw * scale) / 2, offY: (sh - vh * scale) / 2, vw, vh };
}

/** Map a normalized (unmirrored) video-space point to canvas pixels. */
function mapPoint(m: CoverMap, vx: number, vy: number, mirrored: boolean): { x: number; y: number } {
  const dispW = m.vw * m.scale;
  const dispH = m.vh * m.scale;
  return { x: m.offX + dispW * (mirrored ? 1 - vx : vx), y: m.offY + dispH * vy };
}

interface DrawSpec {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  anchorU: number;
  anchorV: number;
}

/** Where each SVG's attachment point sits within its own viewBox, and its aspect (h/w). */
const FILTER_ANCHOR: Record<OverlayFilterId, { u: number; v: number; aspect: number }> = {
  mustache: { u: 0.5, v: 20 / 90, aspect: 90 / 200 },
  "clown-nose": { u: 0.5, v: 0.52, aspect: 1 }, // matches the SVG's circle cy=52 (viewBox 100 tall)
  "pig-nose": { u: 0.5, v: 48 / 90, aspect: 90 / 120 },
  glasses: { u: 0.5, v: 0.55, aspect: 100 / 300 },
  tears: { u: 0.5, v: 4 / 140, aspect: 140 / 40 },
};

/** True only for a genuinely drawable image — a BROKEN image still has complete===true. */
function isDrawable(img: HTMLImageElement | undefined): img is HTMLImageElement {
  return !!img && img.complete && img.naturalWidth > 0;
}

function drawSpec(ctx: CanvasRenderingContext2D, img: HTMLImageElement, spec: DrawSpec): void {
  ctx.save();
  ctx.translate(spec.x, spec.y);
  ctx.rotate(spec.rotation);
  ctx.drawImage(img, -spec.width * spec.anchorU, -spec.height * spec.anchorV, spec.width, spec.height);
  ctx.restore();
}

export class PenaltyStage {
  private filter: FilterId | null = null;
  private running = false;
  private rafId = -1;
  private lastDriveAt = -Infinity;
  private lastFrameAt = -1;
  private frameCount = 0;
  private sized = false;
  private latestBlob: Blob | null = null;
  private lastLandmarks: NormalizedLandmark[] | null = null;
  private lastSeenAt = -Infinity;
  private readonly imageCache = new Map<OverlayFilterId, HTMLImageElement>();
  private readonly smoothState = new Map<string, number>();
  private readonly warp: WarpRenderer;

  constructor(private readonly opts: PenaltyStageOptions) {
    this.warp = new WarpRenderer({
      container: opts.elements.container,
      before: opts.elements.canvas,
      video: opts.elements.video,
    });
  }

  /** Attach (or clear) the video source. Safe to call before or after start(). */
  setStream(stream: MediaStream | null): void {
    this.opts.elements.video.srcObject = stream;
    if (stream) void this.opts.elements.video.play().catch(() => {});
  }

  setFilter(filter: FilterId | null): void {
    if (filter !== this.filter) this.smoothState.clear(); // don't blend anchors across shapes
    this.filter = filter;
    if (filter === null) {
      this.warp.setFilter(null);
      return;
    }
    if (isWarpFilterId(filter)) {
      this.warp.setFilter(filter);
      void this.warp.ensure(); // lazy Pixi Application creation on first warp selection
    } else {
      this.warp.setFilter(null);
      // Label ids have no image asset — they're drawn directly via fillText
      // in drawLabel(), nothing to preload into imageCache.
      if (isOverlayFilterId(filter)) void this.ensureImage(filter);
    }
  }

  /** Show/hide the whole stage box (container). Independent of start()/stop() — a caller may
   *  want the stage running-but-hidden (e.g. behind a "no video" placeholder). */
  setVisible(visible: boolean): void {
    this.opts.elements.container.classList.toggle("hidden", !visible);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    // Best-effort: canvas fillText only uses a font once the document has it
    // loaded — this game's own CSS already requests "Black Han Sans" for the
    // h1/score UI, so it's almost always warm by the time a stage starts, but
    // this fire-and-forget nudge covers the cold-start label filter case (the
    // first frames just fall back to sans-serif and self-correct either way).
    void document.fonts.load('bold 40px "Black Han Sans"').catch(() => {});
    const loop = (nowMs: number): void => {
      if (!this.running) return;
      this.frame(nowMs);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
    // No eager refreshBlob() here — frame() triggers the first one itself,
    // right after it sizes the canvas for the first time (`sized`), so the
    // initial snapshot isn't captured against a stale/zero canvas size.
  }

  /** Stop the loop, dispose an owned tracker, and clear the canvas. Idempotent. */
  stop(): void {
    this.running = false;
    if (this.rafId >= 0) cancelAnimationFrame(this.rafId);
    this.rafId = -1;
    this.opts.driveTracker?.close();
    this.warp.destroy();
    this.opts.elements.video.style.opacity = "";
    this.opts.elements.video.srcObject = null;
    const { canvas } = this.opts.elements;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
  }

  /** The most recently pre-generated snapshot (refreshed periodically) — read synchronously in a tap handler. */
  getLatestBlob(): Blob | null {
    return this.latestBlob;
  }

  private async ensureImage(filter: OverlayFilterId): Promise<void> {
    if (this.imageCache.has(filter)) return;
    const img = new Image();
    img.src = `/assets/filters/${filter}.svg`;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve(); // best-effort; isDrawable() guards before every draw
    });
    this.imageCache.set(filter, img);
  }

  private frame(nowMs: number): void {
    const { video, canvas, hint, container } = this.opts.elements;
    const { driveTracker } = this.opts;
    if (driveTracker && nowMs - this.lastDriveAt >= DRIVE_INTERVAL_MS) {
      this.lastDriveAt = nowMs;
      driveTracker.update(nowMs);
    }

    const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
    const wantW = Math.round(canvas.clientWidth * dpr);
    const wantH = Math.round(canvas.clientHeight * dpr);
    if (wantW > 0 && wantH > 0 && (canvas.width !== wantW || canvas.height !== wantH)) {
      canvas.width = wantW;
      canvas.height = wantH;
      if (!this.sized) {
        this.sized = true;
        void this.refreshBlob();
      }
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const lm = this.resolveLandmarks(nowMs);
    const dtMs = this.lastFrameAt < 0 ? 1000 / 60 : nowMs - this.lastFrameAt;
    this.lastFrameAt = nowMs;

    // Warp mode: keep the <video> playing (iOS throttles a display:none video)
    // but invisible — the mesh itself renders the (already-mirrored) frame.
    // The overlay canvas stays free to keep doing its face-lost hint duty below.
    // Gated on warp.ready (not just "is a warp filter selected") so: (1) the
    // very first pick doesn't black-flash the video before the mesh can draw
    // anything, and (2) a mid-session WebGL context loss (warp.ready flips
    // false) automatically un-hides the video and hides the dead canvas —
    // no separate notification path needed, this branch is re-evaluated
    // every tick regardless.
    if (this.filter && isWarpFilterId(this.filter) && this.warp.ready) {
      video.style.opacity = "0";
      this.warp.setVisible(true);
      if (!container.classList.contains("hidden")) {
        this.warp.renderFrame(lm, this.opts.mirrored, dtMs);
      }
    } else {
      video.style.opacity = "";
      this.warp.setVisible(false);
    }

    if (!lm) {
      hint.classList.remove("hidden");
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      hint.classList.add("hidden");
      if (this.filter && isOverlayFilterId(this.filter)) {
        const img = this.imageCache.get(this.filter);
        if (isDrawable(img)) {
          const cover = computeCoverMap(video, canvas.width, canvas.height);
          const specs = this.computeDrawSpecs(this.filter, lm, cover, nowMs, video.videoWidth > 0, false);
          try {
            for (const spec of specs) drawSpec(ctx, img, spec);
          } catch (err) {
            // A "complete" image can still throw (e.g. a corrupt/broken SVG) —
            // skip this filter for this frame rather than killing the rAF loop.
            console.warn("[penalty] drawImage failed, skipping this frame's filter:", err);
          }
        }
      } else if (this.filter && isLabelFilterId(this.filter)) {
        const cover = computeCoverMap(video, canvas.width, canvas.height);
        this.drawLabel(ctx, this.filter, lm, cover, nowMs, video.videoWidth > 0, false);
      }
    }

    this.frameCount++;
    if (this.frameCount % BLOB_REFRESH_FRAMES === 0) void this.refreshBlob();
  }

  /** Applies the FACE_LOST_GRACE_MS blink tolerance on top of the caller's null-on-face-lost signal. */
  private resolveLandmarks(nowMs: number): NormalizedLandmark[] | null {
    const raw = this.opts.getLandmarks();
    if (raw) {
      this.lastLandmarks = raw;
      this.lastSeenAt = nowMs;
      return raw;
    }
    if (nowMs - this.lastSeenAt < FACE_LOST_GRACE_MS) {
      return this.lastLandmarks; // brief blink — keep the last known pose, no flicker
    }
    this.lastLandmarks = null; // grace expired — a later blink-length gap won't reuse this
    return null;
  }

  private smooth(key: string, next: number, skip: boolean): number {
    if (skip) return next; // offscreen snapshot capture must not perturb the live EMA state
    const prev = this.smoothState.get(key);
    const v = prev === undefined ? next : prev + (next - prev) * EMA_ALPHA;
    this.smoothState.set(key, v);
    return v;
  }

  /**
   * Anchor tuning (field-space landmark indices, matching face.ts's mesh):
   *  - mustache: philtrum (164), width = 2.0x mouth width (61↔291), rotation
   *    from the eye line (33→263).
   *  - clown/pig nose: nose tip (1), width = 0.9x inter-eye distance (33↔263).
   *  - glasses: eye-line midpoint, width = 2.3x inter-eye distance, rotation
   *    from the eye line.
   *  - tears: lower eyelids (145 left, 374 right), length scales with face
   *    height (10↔152), slight time-based horizontal wobble.
   * Distances are computed in normalized landmark space (mixing per-axis
   * fractions in one hypot, same approximation face.ts's mouth ellipse
   * already uses) then scaled to canvas pixels via the cover map.
   */
  private computeDrawSpecs(
    filter: OverlayFilterId,
    lm: NormalizedLandmark[],
    cover: CoverMap,
    nowMs: number,
    haveVideoDims: boolean,
    skipSmoothing: boolean,
  ): DrawSpec[] {
    if (!haveVideoDims) return [];
    const mirrored = this.opts.mirrored;
    const meta = FILTER_ANCHOR[filter];
    const pt = (i: number): { x: number; y: number } | null => {
      const p = lm[i];
      return p ? mapPoint(cover, p.x, p.y, mirrored) : null;
    };
    const make = (key: string, anchor: { x: number; y: number }, width: number, rotation: number): DrawSpec => {
      const w = this.smooth(`${key}-w`, width, skipSmoothing);
      return {
        x: this.smooth(`${key}-x`, anchor.x, skipSmoothing),
        y: this.smooth(`${key}-y`, anchor.y, skipSmoothing),
        width: w,
        height: w * meta.aspect,
        rotation: this.smooth(`${key}-r`, rotation, skipSmoothing),
        anchorU: meta.u,
        anchorV: meta.v,
      };
    };

    switch (filter) {
      case "mustache": {
        const anchor = pt(164);
        const eyeL = pt(33);
        const eyeR = pt(263);
        const mouthL = pt(61);
        const mouthR = pt(291);
        if (!anchor || !eyeL || !eyeR || !mouthL || !mouthR) return [];
        const mouthWidth = Math.hypot(mouthR.x - mouthL.x, mouthR.y - mouthL.y);
        const rotation = Math.atan2(eyeR.y - eyeL.y, eyeR.x - eyeL.x);
        return [make("mustache", anchor, mouthWidth * 2.0, rotation)];
      }
      case "clown-nose":
      case "pig-nose": {
        const anchor = pt(1);
        const eyeL = pt(33);
        const eyeR = pt(263);
        if (!anchor || !eyeL || !eyeR) return [];
        const eyeDist = Math.hypot(eyeR.x - eyeL.x, eyeR.y - eyeL.y);
        return [make(filter, anchor, eyeDist * 0.9, 0)];
      }
      case "glasses": {
        const eyeL = pt(33);
        const eyeR = pt(263);
        if (!eyeL || !eyeR) return [];
        const eyeDist = Math.hypot(eyeR.x - eyeL.x, eyeR.y - eyeL.y);
        const mid = { x: (eyeL.x + eyeR.x) / 2, y: (eyeL.y + eyeR.y) / 2 };
        const rotation = Math.atan2(eyeR.y - eyeL.y, eyeR.x - eyeL.x);
        return [make("glasses", mid, eyeDist * 2.3, rotation)];
      }
      case "tears": {
        const faceTop = pt(10);
        const faceBottom = pt(152);
        const leftEye = pt(145);
        const rightEye = pt(374);
        if (!faceTop || !faceBottom || !leftEye || !rightEye) return [];
        const faceHeight = Math.hypot(faceBottom.x - faceTop.x, faceBottom.y - faceTop.y);
        const length = faceHeight * 0.55;
        const width = length / meta.aspect;
        const wobble = Math.sin(nowMs / 400) * faceHeight * 0.02;
        return [
          make("tear-l", { x: leftEye.x + wobble, y: leftEye.y }, width, 0),
          make("tear-r", { x: rightEye.x - wobble, y: rightEye.y }, width, 0),
        ];
      }
    }
  }

  /**
   * Meme-style text label floating above the head (하남자/하여자/포테토남).
   * Anchor: horizontally the eye-line midpoint (33/263), vertically
   * `0.28×faceHeightPx` above the forehead point (10) — same landmark
   * vocabulary/EMA-smoothing convention as the SVG filters above, just drawn
   * with fillText instead of drawImage. No rotation — meme labels read better
   * staying upright regardless of head tilt. `skipSmoothing` doubles as "this
   * is the offscreen snapshot pass" per computeDrawSpecs' own convention, so
   * the gentle float bob is forced to 0 there (a frozen snapshot shouldn't
   * capture an arbitrary bob phase).
   */
  private drawLabel(
    ctx: CanvasRenderingContext2D,
    filter: LabelFilterId,
    lm: NormalizedLandmark[],
    cover: CoverMap,
    nowMs: number,
    haveVideoDims: boolean,
    skipSmoothing: boolean,
  ): void {
    if (!haveVideoDims) return;
    const mirrored = this.opts.mirrored;
    const pt = (i: number): { x: number; y: number } | null => {
      const p = lm[i];
      return p ? mapPoint(cover, p.x, p.y, mirrored) : null;
    };
    const eyeL = pt(33);
    const eyeR = pt(263);
    const forehead = pt(10);
    const chin = pt(152);
    if (!eyeL || !eyeR || !forehead || !chin) return;

    const faceWidthPx = Math.hypot(eyeR.x - eyeL.x, eyeR.y - eyeL.y);
    const faceHeightPx = Math.hypot(chin.x - forehead.x, chin.y - forehead.y);
    const cx = (eyeL.x + eyeR.x) / 2;
    const anchorY = forehead.y - faceHeightPx * 0.28;

    const x = this.smooth("label-x", cx, skipSmoothing);
    const yBase = this.smooth("label-y", anchorY, skipSmoothing);
    const size = this.smooth("label-size", Math.max(22, Math.min(64, faceWidthPx * 0.34)), skipSmoothing);
    const bob = skipSmoothing ? 0 : Math.sin(nowMs / 350) * (size * 0.08);
    const y = yBase + bob;

    const text = FILTERS.find((f) => f.id === filter)?.label ?? filter;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.lineJoin = "round";
    ctx.fillStyle = "#ffd166";
    ctx.strokeStyle = "#101014";

    ctx.font = `bold ${size}px "Black Han Sans", sans-serif`;
    ctx.lineWidth = size / 7;
    ctx.strokeText(text, x, y); // stroke first, then fill — clean outline
    ctx.fillText(text, x, y);

    // "▼" pointer, same style, smaller, with a gap below the text.
    const arrowSize = size * 0.5;
    const arrowY = y + size * 0.3 + arrowSize;
    ctx.font = `bold ${arrowSize}px "Black Han Sans", sans-serif`;
    ctx.lineWidth = arrowSize / 7;
    ctx.strokeText("▼", x, arrowY);
    ctx.fillText("▼", x, arrowY);
    ctx.restore();
  }

  /** Compose the current frame (video + filter + watermark) into a JPEG blob. */
  private async captureSnapshotBlob(): Promise<Blob | null> {
    const { video, canvas } = this.opts.elements;
    if (!video.videoWidth || !video.videoHeight) return null;
    const off = document.createElement("canvas");
    off.width = canvas.width || video.videoWidth;
    off.height = canvas.height || video.videoHeight;
    const ctx = off.getContext("2d");
    if (!ctx) return null;

    const cover = computeCoverMap(video, off.width, off.height);
    const dispW = cover.vw * cover.scale;
    const dispH = cover.vh * cover.scale;

    if (this.filter && isWarpFilterId(this.filter) && this.warp.ready) {
      // Warp mode: the mesh already renders the video AND bakes in the mirror
      // (via UV mapping, not a canvas transform) — draw it as-is, no separate
      // video pass and no extra ctx.scale(-1,1) (that would double-mirror it).
      this.warp.render(); // force a fresh GPU render before reading pixels
      const warpCanvas = this.warp.canvasEl;
      if (warpCanvas) ctx.drawImage(warpCanvas, 0, 0, off.width, off.height);
    } else {
      // Video frame, cover-fit (+ mirrored for the loser-self view). WebRTC
      // remote video never taints the canvas as long as peerIdentity is never
      // set on the RTCPeerConnection (it never is — see rtc.ts).
      ctx.save();
      if (this.opts.mirrored) {
        ctx.translate(cover.offX + dispW, cover.offY);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, dispW, dispH);
      } else {
        ctx.drawImage(video, cover.offX, cover.offY, dispW, dispH);
      }
      ctx.restore();

      // Filter overlay — specs are already in this same (possibly-mirrored) canvas
      // space. skipSmoothing=true: this offscreen capture must not perturb the
      // live overlay's EMA state (it runs off-cadence from the live frame loop).
      const lm = this.lastLandmarks;
      if (lm && this.filter && isOverlayFilterId(this.filter)) {
        const img = this.imageCache.get(this.filter);
        if (isDrawable(img)) {
          try {
            for (const spec of this.computeDrawSpecs(this.filter, lm, cover, performance.now(), true, true)) {
              drawSpec(ctx, img, spec);
            }
          } catch (err) {
            console.warn("[penalty] drawImage failed while capturing a snapshot, skipping the filter:", err);
          }
        }
      } else if (lm && this.filter && isLabelFilterId(this.filter)) {
        // skipSmoothing=true forces the float bob to phase 0 (see drawLabel's doc).
        this.drawLabel(ctx, this.filter, lm, cover, performance.now(), true, true);
      }
    }

    // Watermark, bottom-right.
    ctx.font = "600 16px sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillText(WATERMARK_TEXT, off.width - 9, off.height - 9);
    ctx.fillStyle = "#ffd166";
    ctx.fillText(WATERMARK_TEXT, off.width - 10, off.height - 10);

    return new Promise((resolve) => off.toBlob((b) => resolve(b), "image/jpeg", 0.92));
  }

  private async refreshBlob(): Promise<void> {
    const blob = await this.captureSnapshotBlob();
    if (blob) this.latestBlob = blob;
  }
}
